// src/components/roles/PermissionsModal.js
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { customFormDataPost } from "../../helper/api-helper/apiHerlpers";
import apiUrls from "../../constants/apiUrls";
import apis from "../../services/apis";
import { useSmarkState } from "../../Context/KeycloakContext";
import apiCommonParams from "../../constants/apiCommonParams";

const PermissionsModal = ({
  show,
  onHide,
  role,
  applicationId,
  siteId,
  moduleId = 0,
}) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const userDetails = useSmarkState();
  const storedToken = localStorage.getItem("keycloakToken");
  const isEditable =
  role?.applicationId === applicationId &&
  role?.siteId === siteId;


  /* ========= LOAD PERMISSIONS ========= */
  useEffect(() => {
    if (show && role) {
      loadPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, role]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      
      const allRes = await apis.getRolePermissions(
        applicationId,
        siteId,
        moduleId,
        storedToken
      );

      const allPermissions =
        JSON.parse(allRes.json)?.permissionTOList || [];

      
      const assignedRes = await apis.getAssignedPermissions(
        applicationId,
        siteId,
        role.roleId,
        storedToken
      );

      const assignedPermissions =
        JSON.parse(assignedRes.json)?.permissionTOList || [];

      const assignedIds = new Set(
        assignedPermissions.map((p) => p.permissionId)
      );

      const merged = allPermissions.map((perm) => ({
        ...perm,
        checked: assignedIds.has(perm.permissionId),
      }));

      setPermissions(merged);
      setInitialPermissions(merged);
    } catch (err) {
      console.error("Permission load error", err);
      toast.error("Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };
    const hasChanges = permissions.some((perm, index) =>
      perm.checked !== initialPermissions[index]?.checked
    );

  /* ========= TOGGLE ========= */
  const togglePermission = (permissionId) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.permissionId === permissionId
          ? { ...p, checked: !p.checked }
          : p
      )
    );
  };

  /* ========= SAVE / REMOVE PERMISSIONS (DELETE-ROLE STYLE) ========= */
  const savePermissions = async () => {
    setSaving(true);

    try {
      const selectedPermissionIds = permissions
        .filter((p) => p.checked)
        .map((p) => p.permissionId)
        .join(",");

      const formData = new FormData();
      formData.append("applicationId", applicationId);
      formData.append("siteId", siteId);
      formData.append("roleId", role.roleId);
      formData.append("permissionIds", selectedPermissionIds);
      formData.append("performedByUserId", userDetails.userId);
      formData.append("swcmTicket", apiCommonParams.swcmTicket);

      const result = await customFormDataPost(
        apiUrls.savePermission,
        formData,
        storedToken
      );

      if (result?.code === "SUCCESS") {
        toast.success("Permissions updated successfully");
        setInitialPermissions(permissions);
        
      } else {
        toast.error(result?.message || "Failed to update permissions");
      }
    } catch (err) {
      console.error("Save permission error", err);
      toast.error("Something went wrong while saving permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Permissions – {role?.roleName}
        </Modal.Title>
      </Modal.Header>

     <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <Form>
                {permissions.map((perm) => (
                  <Form.Check
                    key={perm.permissionId}
                    type="checkbox"
                    label={`${perm.permissionDesc}`}
                    checked={perm.checked}
                    disabled={!isEditable}
                    onChange={() => togglePermission(perm.permissionId)}
                  />
                ))}
              </Form>

              {!isEditable && (
                <small className="text-muted d-block mt-2">
                  Permissions cannot be modified for roles outside the current
                  site/application.
                </small>
              )}
            </>
          )}
        </Modal.Body>


      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={savePermissions}
          disabled={saving || !isEditable || !hasChanges}
        >
          {saving ? "Saving..." : "Save"}
        </Button>

      </Modal.Footer>
    </Modal>
  );
};

export default PermissionsModal;
